import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Badge, Alert, Input } from '../components/design-system';
import { ArrowLeft, CheckCircle, XCircle, Send, Archive, RefreshCw, Save, Eye, ExternalLink, Edit3 } from 'lucide-react';

export const ArticleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEditor, isAdmin } = useAuth();
  const [article, setArticle] = useState(null);
  const [sourceItems, setSourceItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadArticle(); }, [id]);

  const loadArticle = async () => {
    try {
      const res = await api.getArticle(id);
      setArticle(res.article);
      setSourceItems(res.sourceItems || []);
      setEditForm({
        headline: res.article.headline,
        subheadline: res.article.subheadline || '',
        body: res.article.body,
        summary: res.article.summary || '',
      });
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleAction = async (action) => {
    setError(''); setSuccess('');
    try {
      const actions = {
        approve: () => api.approveArticle(id),
        publish: () => api.publishArticle(id),
        reject: () => api.rejectArticle(id),
        archive: () => api.archiveArticle(id),
        regenerate: () => api.regenerateArticle(id),
      };
      await actions[action]();
      setSuccess(`Article ${action}d successfully`);
      loadArticle();
    } catch (err) { setError(err.message); }
  };

  const handleSave = async () => {
    setError(''); setSuccess('');
    try {
      await api.updateArticle(id, editForm);
      setSuccess('Article updated');
      setEditing(false);
      loadArticle();
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this article?')) return;
    try { await api.deleteArticle(id); navigate('/articles'); }
    catch (err) { setError(err.message); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>;
  if (!article) return <div className="text-center py-20 text-gray-500">Article not found</div>;

  const statusVariant = { draft: 'default', review: 'warning', approved: 'info', published: 'success', rejected: 'danger', archived: 'default' };
  const topics = safeParseArray(article.topics);

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/articles')} className="p-2 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="h-4 w-4 text-gray-500" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Badge variant={statusVariant[article.editorial_status] || 'default'}>{article.editorial_status}</Badge>
            <Badge>{article.category}</Badge>
            {article.api_views > 0 && <span className="text-xs text-gray-400 flex items-center gap-1"><Eye className="h-3 w-3" /> {article.api_views} views</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {isEditor && !editing && <Button variant="secondary" size="sm" icon={Edit3} onClick={() => setEditing(true)}>Edit</Button>}
          {isEditor && article.editorial_status === 'draft' && <Button variant="secondary" size="sm" icon={CheckCircle} onClick={() => handleAction('approve')}>Approve</Button>}
          {isEditor && article.editorial_status === 'review' && <Button variant="secondary" size="sm" icon={CheckCircle} onClick={() => handleAction('approve')}>Approve</Button>}
          {isAdmin && (article.editorial_status === 'approved' || article.editorial_status === 'review') && <Button size="sm" icon={Send} onClick={() => handleAction('publish')}>Publish</Button>}
          {isEditor && article.editorial_status !== 'rejected' && article.editorial_status !== 'archived' && (
            <Button variant="ghost" size="sm" icon={XCircle} onClick={() => handleAction('reject')}>Reject</Button>
          )}
          {isEditor && <Button variant="ghost" size="sm" icon={RefreshCw} onClick={() => handleAction('regenerate')}>Regenerate</Button>}
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Article Content */}
      <Card>
        {editing ? (
          <div className="space-y-4">
            <Input label="Headline" value={editForm.headline} onChange={(e) => setEditForm({ ...editForm, headline: e.target.value })} />
            <Input label="Subheadline" value={editForm.subheadline} onChange={(e) => setEditForm({ ...editForm, subheadline: e.target.value })} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
              <textarea className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 min-h-[300px]"
                value={editForm.body} onChange={(e) => setEditForm({ ...editForm, body: e.target.value })} />
            </div>
            <Input label="Summary" value={editForm.summary} onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
              <Button icon={Save} onClick={handleSave}>Save</Button>
            </div>
          </div>
        ) : (
          <article className="prose prose-sm max-w-none">
            <h1 className="text-xl font-bold text-gray-900 mb-1">{article.headline}</h1>
            {article.subheadline && <p className="text-base text-gray-600 mt-0 mb-4">{article.subheadline}</p>}
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{article.body}</div>
            {article.summary && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 font-medium mb-1">Summary</p>
                <p className="text-sm text-gray-600">{article.summary}</p>
              </div>
            )}
          </article>
        )}
      </Card>

      {/* Metadata */}
      <Card className="!p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><span className="text-xs text-gray-500 block">Sources</span><span className="font-medium">{article.source_count}</span></div>
          <div><span className="text-xs text-gray-500 block">Created</span><span className="font-medium">{new Date(article.created_at).toLocaleString()}</span></div>
          {article.published_at && <div><span className="text-xs text-gray-500 block">Published</span><span className="font-medium">{new Date(article.published_at).toLocaleString()}</span></div>}
          {article.slug && <div><span className="text-xs text-gray-500 block">Slug</span><span className="font-medium text-xs">{article.slug}</span></div>}
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500 block mb-1">AI Model</span>
          <code className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
            {article.model_used || 'unknown'}
          </code>
        </div>
        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-100">
            {topics.map((t, i) => <Badge key={i} variant="primary">{t}</Badge>)}
          </div>
        )}
      </Card>

      {/* Source Items */}
      {sourceItems.length > 0 && (
        <Card>
          <h3 className="text-base font-semibold text-gray-900 mb-3">Source Items ({sourceItems.length})</h3>
          <div className="space-y-2">
            {sourceItems.map(item => (
              <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate">{item.title}</div>
                  <div className="text-xs text-gray-400">{item.source_name}</div>
                </div>
                {item.url && (
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-gray-100 rounded">
                    <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Danger Zone */}
      {isAdmin && (
        <Card className="!border-red-200 !p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-red-700">Danger Zone</h4>
              <p className="text-xs text-red-500">Permanently delete this article</p>
            </div>
            <Button variant="danger" size="sm" onClick={handleDelete}>Delete Article</Button>
          </div>
        </Card>
      )}
    </div>
  );
};

function safeParseArray(str) { try { return JSON.parse(str || '[]'); } catch { return []; } }
